// CSR-704 — Render-time formatter for the per-policy activity timeline.
//
// Pure-function module consumed by both the primary
// (`PolicyActivitiesTab.tsx`) and legacy (`PolicyDetail.tsx`) renderers so
// they produce identical content. No React, no I/O, no state — every
// export is a pure function or a constant table.

import {
  isCarrierSyncSupported,
  type PolicyVehicle,
  type PolicyTrailer,
  type PolicyDriver,
} from "@shared/schema";
import { isRealValue } from "@shared/coverage-rules";
import { syncAttemptUnavailableMessage } from "@shared/sync-attempt";
import { formatPolicySubstatusLabel, isPolicySubstatus } from "@shared/policy-substatus";
import {
  formatDate,
  formatLastUpdated,
  formatNoteDateTime,
} from "@/lib/formatters";
import { getDriverDisplayName } from "@/lib/driver-display";
import { isNonOwnedAttachedTrailer } from "@/components/insured/assets/asset-helpers";
import type { PolicyChangeHistoryEntry, PolicySyncEvent } from "@/lib/api";

export interface PolicyContext {
  carrier: string;
  parentCarrier?: string | null;
}

export type FormattedDiffKind =
  | "scalar"
  | "currency"
  | "date"
  | "status"
  | "coverage_subfield"
  | "asset_aggregate"
  | "asset_item";

export interface FormattedDiff {
  label: string;
  before: string;
  after: string;
  changed: boolean;
  kind: FormattedDiffKind;
}

export const FIELD_LABELS: Record<string, string> = {
  policyNumber: "Policy Number",
  customerName: "Customer Name",
  carrier: "Carrier",
  parentCarrier: "Parent Carrier",
  status: "Status",
  substatus: "Substatus",
  producer: "Producer",
  type: "Type",
  effectiveDate: "Effective Date",
  expirationDate: "Expiration Date",
  boundDate: "Bound Date",
  cancellationDate: "Cancellation Date",
  premium: "Premium",
  // CSR-2344 — the policyFees field is labeled Finance Charge in the UI; the
  // key (and DC's policy_fees column) keep their old name.
  policyFees: "Finance Charge",
  dotNumber: "DOT Number",
  agency: "Agency",
  companyEmail: "Company Email",
  vehicles: "Vehicles",
  trailers: "Trailers",
  drivers: "Drivers",
  coverages: "Coverages",
  mga: "MGA",
  financeCompany: "Finance Company",
  paymentPlan: "Payment Plan",
  carrierNaic: "Carrier NAIC",
  // CSR-2326 — the DC AOR flag (customer moved agency).
  aorIdentifiedAt: "AOR Identified",
  aorSource: "AOR Source",
  carrierAgencyCode: "Carrier Agency Code",
};

export const COVERAGE_LABELS: Record<string, string> = {
  generalLiability: "General Liability",
  autoLiability: "Auto Liability",
  umbrella: "Umbrella",
  workersComp: "Workers Compensation",
  cargo: "Motor Truck Cargo",
  physicalDamage: "Physical Damage",
  nonTruckingLiability: "Non-Trucking Liability",
  hiredAutoLiability: "Hired Auto Liability",
  hiredNonOwnedLiability: "Hired Non-Owned Liability",
  onHook: "On-Hook",
};

export const SUBFIELD_LABELS: Record<string, string> = {
  eachOccurrence: "Each Occurrence Limit",
  damageToRented: "Damage to Rented Premises",
  medExp: "Medical Expenses",
  personalAdvInjury: "Personal & Advertising Injury",
  generalAggregate: "General Aggregate",
  productsCompOpAgg: "Products / Completed Ops Aggregate",
  combinedSingleLimit: "Combined Single Limit",
  bodilyInjuryPerPerson: "Bodily Injury — Per Person",
  bodilyInjuryPerAccident: "Bodily Injury — Per Accident",
  propertyDamage: "Property Damage",
  comprehensiveDeductible: "Comprehensive Deductible",
  collisionDeductible: "Collision Deductible",
  deductible: "Deductible",
  limit: "Limit",
  aggregate: "Aggregate",
  elEachAccident: "Employer's Liability — Per Accident",
  elDiseaseEaEmployee: "Employer's Liability — Disease (Per Employee)",
  elDiseasePolicyLimit: "Employer's Liability — Disease (Policy Limit)",
  autoType: "Auto Type",
  claimsMade: "Claims Made",
  aggregateAppliesPer: "Aggregate Applies Per",
  perStatute: "Per Statute",
  isExcess: "Is Excess",
  status: "Status",
};

// CSR-1844 — exported for reuse by the Activity Log page renderer
// (activity-log-format.ts) so "(empty)" and 2-decimal money have one source.
export const EMPTY_DISPLAY = "(empty)";
// CSR-1939 — the "after" token for a structured field (coverages / asset list)
// whose data can't be diffed sub-field-by-sub-field (genuinely unparseable or
// wrong-shaped). Surfaces that the field changed WITHOUT ever rendering the raw
// JSON blob the Event Logging Spec forbids in the UI.
const UPDATED_DISPLAY = "(updated)";
const NO_FUNCTIONAL_CHANGE = "—";
const CURRENCY_SHAPED = /^\$?[\d,]*(\.\d+)?$/;

function titleCase(key: string): string {
  if (!key) return key;
  const spaced = key.replace(/([A-Z])/g, " $1").replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

export function formatCurrencyValue(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined || raw === "") return EMPTY_DISPLAY;
  const num = typeof raw === "number" ? raw : Number(String(raw).replace(/[$,]/g, ""));
  if (!isFinite(num)) return EMPTY_DISPLAY;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function parseSafely<T>(raw: string | null, field: string, policyId: string): T | null {
  if (raw === null || raw === undefined || raw === "") return null;
  try {
    return JSON.parse(raw) as T;
  } catch (err) {
    // Spec must-do: never silently swallow.
    // eslint-disable-next-line no-console
    console.warn("[policy-activity-formatter] failed to parse JSON", {
      field,
      policyId,
      raw,
      error: err instanceof Error ? err.message : String(err),
    });
    return null;
  }
}

// CSR-1939 — coverages parse result. `parseSafely` conflates an EMPTY side
// (null / "") with a genuine parse failure by returning `null` for both, which
// forced the whole diff into the scalar fallback the moment coverages synced in
// for the first time (empty -> populated) — dumping the populated JSON blob.
// This 3-way result keeps them distinct: an empty side becomes `{}` and diffs
// normally; only a genuinely unparseable / wrong-shaped side falls back.
type CoverageSide =
  | { state: "empty" }
  | { state: "object"; value: Record<string, unknown> }
  | { state: "invalid" };

function parseCoverageSide(raw: string | null, policyId: string): CoverageSide {
  if (raw === null || raw === undefined || raw === "") return { state: "empty" };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    // Spec must-do: never silently swallow.
    // eslint-disable-next-line no-console
    console.warn("[policy-activity-formatter] failed to parse JSON", {
      field: "coverages",
      policyId,
      raw,
      error: err instanceof Error ? err.message : String(err),
    });
    return { state: "invalid" };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { state: "invalid" };
  }
  return { state: "object", value: parsed as Record<string, unknown> };
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return a === b;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  const ak = Object.keys(a as Record<string, unknown>);
  const bk = Object.keys(b as Record<string, unknown>);
  if (ak.length !== bk.length) return false;
  for (const key of ak) {
    if (!deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) {
      return false;
    }
  }
  return true;
}

function formatSubfieldValue(
  raw: unknown,
  hasCurrencySibling: boolean,
): string {
  if (raw === null || raw === undefined) return EMPTY_DISPLAY;
  if (typeof raw === "boolean") return raw ? "Yes" : "No";
  if (typeof raw === "number") {
    if (!isFinite(raw)) return EMPTY_DISPLAY;
    // Currency-likely numeric → format as currency. The coverage tables
    // store all monetary fields as strings, so this branch is mostly for
    // legacy rows that wrote raw numbers.
    return `$${raw.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (typeof raw === "string") {
    if (raw === "") {
      return hasCurrencySibling ? "$0.00" : EMPTY_DISPLAY;
    }
    if (CURRENCY_SHAPED.test(raw)) return formatCurrencyValue(raw);
    return raw;
  }
  return String(raw);
}

function blockHasCurrencyShape(
  oldBlock: Record<string, unknown> | null,
  newBlock: Record<string, unknown> | null,
  subKey: string,
): boolean {
  // Empty values render as "$0.00" only when at least one side of the same
  // coverage block stores currency-shaped data on a sibling field — protects
  // unrelated boolean/string fields from being mis-rendered as money.
  const blocks = [oldBlock, newBlock];
  for (const block of blocks) {
    if (!block) continue;
    for (const [k, v] of Object.entries(block)) {
      if (k === subKey) continue;
      if (typeof v === "string" && v !== "" && CURRENCY_SHAPED.test(v)) return true;
      if (typeof v === "number" && isFinite(v)) return true;
    }
  }
  return false;
}

function diffCoverages(
  oldRaw: string | null,
  newRaw: string | null,
  policyId: string,
): FormattedDiff[] {
  const oldSide = parseCoverageSide(oldRaw, policyId);
  const newSide = parseCoverageSide(newRaw, policyId);

  // A genuinely unparseable / wrong-shaped side can't be diffed field-by-field.
  // Never render the raw JSON blob (CSR-1939) — emit one neutral line that just
  // says coverages changed. `before`/`after` differ so the line isn't dropped
  // as a display no-op.
  if (oldSide.state === "invalid" || newSide.state === "invalid") {
    return [
      {
        label: FIELD_LABELS.coverages ?? "Coverages",
        before: EMPTY_DISPLAY,
        after: UPDATED_DISPLAY,
        changed: true,
        kind: "scalar",
      },
    ];
  }

  // CSR-1939 — an empty side (null / "") is an empty coverage object, so a
  // first-time sync (empty -> populated) or a full clear (populated -> empty)
  // runs the normal sub-field diff below and produces human-readable bullets.
  const oldParsed: Record<string, unknown> = oldSide.state === "object" ? oldSide.value : {};
  const newParsed: Record<string, unknown> = newSide.state === "object" ? newSide.value : {};

  const out: FormattedDiff[] = [];
  const coverageKeys = Array.from(
    new Set<string>([...Object.keys(oldParsed), ...Object.keys(newParsed)]),
  );

  for (const coverageKey of coverageKeys) {
    const oldBlock = oldParsed[coverageKey];
    const newBlock = newParsed[coverageKey];
    if (deepEqual(oldBlock, newBlock)) continue;

    const oldObj =
      oldBlock && typeof oldBlock === "object" && !Array.isArray(oldBlock)
        ? (oldBlock as Record<string, unknown>)
        : null;
    const newObj =
      newBlock && typeof newBlock === "object" && !Array.isArray(newBlock)
        ? (newBlock as Record<string, unknown>)
        : null;

    if (!oldObj && !newObj) continue;

    const subKeys = Array.from(
      new Set<string>([
        ...(oldObj ? Object.keys(oldObj) : []),
        ...(newObj ? Object.keys(newObj) : []),
      ]),
    );

    const coverageLabel = COVERAGE_LABELS[coverageKey] ?? titleCase(coverageKey);

    for (const subKey of subKeys) {
      const oldVal = oldObj ? oldObj[subKey] : undefined;
      const newVal = newObj ? newObj[subKey] : undefined;
      if (deepEqual(oldVal, newVal)) continue;

      // CSR-1939 follow-up — suppress a subfield transition where both sides are
      // empty-equivalent ($0.00 === empty). Reuses the canonical coverage
      // "real value" rule (non-empty, not a zero sentinel) so this cannot drift
      // from the display/COI copies. A real change (e.g. $1,000 -> $0.00, or
      // (empty) -> $750,000) still renders.
      if (!isRealValue(oldVal) && !isRealValue(newVal)) continue;

      const subLabel = SUBFIELD_LABELS[subKey] ?? titleCase(subKey);
      const hasSibling = blockHasCurrencyShape(oldObj, newObj, subKey);

      out.push({
        label: `${coverageLabel} — ${subLabel}`,
        before: formatSubfieldValue(oldVal, hasSibling),
        after: formatSubfieldValue(newVal, hasSibling),
        changed: true,
        kind: "coverage_subfield",
      });
    }
  }

  if (out.length === 0) {
    // Both sides parsed and were canonically equal — defensive fallback so
    // the renderer can still show something rather than dropping the row.
    return [
      {
        label: "Coverages",
        before: NO_FUNCTIONAL_CHANGE,
        after: NO_FUNCTIONAL_CHANGE,
        changed: false,
        kind: "coverage_subfield",
      },
    ];
  }

  return out;
}

function isAssetField(field: string): boolean {
  return field === "vehicles" || field === "trailers" || field === "drivers";
}

function keyForVehicleEntry(v: PolicyVehicle, idx: number): string {
  if (v.vin && v.vin.trim()) return v.vin.trim();
  const dbId = (v as { databaseId?: string }).databaseId;
  if (dbId) return String(dbId);
  return `__idx_${idx}`;
}

function keyForDriverEntry(d: PolicyDriver, idx: number): string {
  const name = (d.name ?? "").trim();
  if (name) return name.toLowerCase();
  if (d.licenseNumber && d.licenseNumber.trim()) return d.licenseNumber.trim().toLowerCase();
  return `__idx_${idx}`;
}

function keyForTrailerEntry(t: PolicyTrailer, idx: number): string {
  return keyForVehicleEntry(t, idx);
}

function vehicleLabel(v: PolicyVehicle): string {
  const nonOwned = isNonOwnedAttachedTrailer(v);
  const parts = [
    nonOwned ? undefined : v.year,
    v.make,
    v.model,
  ].filter((p): p is string | number => p !== undefined && p !== "");
  const base = parts.join(" ").trim() || "Unknown vehicle";
  if (v.vin && !nonOwned) return `${base} (${v.vin})`;
  return base;
}

function driverLabel(d: PolicyDriver): string {
  return getDriverDisplayName(d);
}

function trailerLabel(t: PolicyTrailer): string {
  const nonOwned = isNonOwnedAttachedTrailer(t);
  const parts = [
    nonOwned ? undefined : t.year,
    t.make,
    t.model,
  ].filter((p): p is string | number => p !== undefined && p !== "");
  const base = parts.join(" ").trim() || "Unknown trailer";
  if (t.vin && !nonOwned) return `${base} (${t.vin})`;
  return base;
}

function nounFor(field: string, n: number): string {
  if (field === "vehicles") return n === 1 ? "vehicle" : "vehicles";
  if (field === "trailers") return n === 1 ? "trailer" : "trailers";
  if (field === "drivers") return n === 1 ? "driver" : "drivers";
  return field;
}

interface AssetItemBase {
  addedAt?: string;
  removedAt?: string | null;
}

function isLiveOnSide(item: AssetItemBase | undefined): boolean {
  if (!item) return false;
  return item.removedAt === null || item.removedAt === undefined;
}

function formatItemValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return EMPTY_DISPLAY;
  return String(value);
}

function subfieldChanges(
  oldItem: Record<string, unknown>,
  newItem: Record<string, unknown>,
): Array<{ subKey: string; oldVal: unknown; newVal: unknown }> {
  const keys = Array.from(
    new Set<string>([...Object.keys(oldItem), ...Object.keys(newItem)]),
  );
  const changes: Array<{ subKey: string; oldVal: unknown; newVal: unknown }> = [];
  for (const key of keys) {
    if (key === "addedAt" || key === "removedAt") continue;
    if (!deepEqual(oldItem[key], newItem[key])) {
      changes.push({ subKey: key, oldVal: oldItem[key], newVal: newItem[key] });
    }
  }
  return changes;
}

function labelFor(field: string, item: unknown): string {
  if (field === "drivers") return driverLabel(item as PolicyDriver);
  if (field === "trailers") return trailerLabel(item as PolicyTrailer);
  return vehicleLabel(item as PolicyVehicle);
}

function emitItemList(
  verb: "added" | "removed",
  field: string,
  items: unknown[],
): FormattedDiff[] {
  const out: FormattedDiff[] = [];
  if (items.length === 0) return out;

  const count = items.length;
  const noun = nounFor(field, count);

  // Compact sets stay on a single line: "N nouns added: a, b, c".
  if (count <= 3) {
    const names = items.map((it) => labelFor(field, it)).join(", ");
    out.push({
      label: `${count} ${noun} ${verb}: ${names}`,
      before: "",
      after: "",
      changed: true,
      kind: "asset_aggregate",
    });
    return out;
  }

  // Larger sets: aggregate header followed by every item, no truncation.
  out.push({
    label: `${count} ${noun} ${verb}`,
    before: "",
    after: "",
    changed: true,
    kind: "asset_aggregate",
  });
  for (const item of items) {
    out.push({
      label: labelFor(field, item),
      before: "",
      after: "",
      changed: true,
      kind: "asset_item",
    });
  }
  return out;
}

function emitModifiedList(
  field: string,
  modified: Array<{ oldItem: Record<string, unknown>; newItem: Record<string, unknown> }>,
): FormattedDiff[] {
  const out: FormattedDiff[] = [];
  if (modified.length === 0) return out;

  const count = modified.length;
  const noun_ = nounFor(field, count);
  out.push({
    label: `${count} ${noun_} updated`,
    before: "",
    after: "",
    changed: true,
    kind: "asset_aggregate",
  });

  for (const { oldItem, newItem } of modified) {
    const baseLabel = labelFor(field, newItem);
    const changes = subfieldChanges(oldItem, newItem);
    if (changes.length === 0) {
      out.push({
        label: baseLabel,
        before: "",
        after: "",
        changed: true,
        kind: "asset_item",
      });
      continue;
    }
    for (const change of changes) {
      const subLabel = SUBFIELD_LABELS[change.subKey] ?? titleCase(change.subKey);
      const before = formatItemValue(change.oldVal);
      const after = formatItemValue(change.newVal);
      out.push({
        label: `${baseLabel} — ${subLabel} changed from ${before} to ${after}`,
        before,
        after,
        changed: true,
        kind: "asset_item",
      });
    }
  }
  return out;
}

function diffAssetArray(
  field: "vehicles" | "trailers" | "drivers",
  oldRaw: string | null,
  newRaw: string | null,
  policyId: string,
): FormattedDiff[] {
  const oldParsed = parseSafely<unknown>(oldRaw, field, policyId);
  const newParsed = parseSafely<unknown>(newRaw, field, policyId);

  // Treat null/empty as []; treat parse failures as a fallback.
  const oldArr = Array.isArray(oldParsed) ? oldParsed : oldParsed === null ? [] : null;
  const newArr = Array.isArray(newParsed) ? newParsed : newParsed === null ? [] : null;

  if (oldArr === null || newArr === null) {
    // A side that parsed to a non-array (wrong shape) can't be diffed item by
    // item. Never render the raw JSON blob (CSR-1939) — emit one neutral line.
    // (`parseSafely` already warns on a genuine parse failure.)
    return [
      {
        label: FIELD_LABELS[field] ?? titleCase(field),
        before: EMPTY_DISPLAY,
        after: UPDATED_DISPLAY,
        changed: true,
        kind: "scalar",
      },
    ];
  }

  const keyFor = (item: unknown, idx: number): string => {
    if (field === "drivers") return keyForDriverEntry(item as PolicyDriver, idx);
    if (field === "trailers") return keyForTrailerEntry(item as PolicyTrailer, idx);
    return keyForVehicleEntry(item as PolicyVehicle, idx);
  };

  const oldByKey = new Map<string, AssetItemBase & Record<string, unknown>>();
  oldArr.forEach((item, idx) => {
    if (item && typeof item === "object") {
      oldByKey.set(keyFor(item, idx), item as AssetItemBase & Record<string, unknown>);
    }
  });
  const newByKey = new Map<string, AssetItemBase & Record<string, unknown>>();
  newArr.forEach((item, idx) => {
    if (item && typeof item === "object") {
      newByKey.set(keyFor(item, idx), item as AssetItemBase & Record<string, unknown>);
    }
  });

  const added: unknown[] = [];
  const removed: unknown[] = [];
  const modified: Array<{
    oldItem: Record<string, unknown>;
    newItem: Record<string, unknown>;
  }> = [];

  // Added / modified / removed-via-flip pass over `newByKey`.
  for (const [key, newItem] of Array.from(newByKey.entries())) {
    const oldItem = oldByKey.get(key);
    if (!oldItem) {
      if (isLiveOnSide(newItem)) added.push(newItem);
      else removed.push(newItem);
      continue;
    }
    // Both sides present.
    if (isLiveOnSide(oldItem) && !isLiveOnSide(newItem)) {
      removed.push(newItem);
      continue;
    }
    if (!isLiveOnSide(oldItem) && isLiveOnSide(newItem)) {
      added.push(newItem);
      continue;
    }
    if (isLiveOnSide(oldItem) && isLiveOnSide(newItem)) {
      // Possibly modified — check non-lifecycle sub-fields.
      if (subfieldChanges(oldItem, newItem).length > 0) {
        modified.push({ oldItem, newItem });
      }
    }
    // both removed → no-op (already removed previously).
  }

  // Removed pass over `oldByKey` (items not present at all in `newByKey`).
  for (const [key, oldItem] of Array.from(oldByKey.entries())) {
    if (newByKey.has(key)) continue;
    if (isLiveOnSide(oldItem)) removed.push(oldItem);
  }

  const out: FormattedDiff[] = [];
  if (added.length > 0) {
    out.push(...emitItemList("added", field, added));
  }
  if (removed.length > 0) {
    out.push(...emitItemList("removed", field, removed));
  }
  if (modified.length > 0) {
    out.push(...emitModifiedList(field, modified));
  }

  if (out.length === 0) {
    return [
      {
        label: FIELD_LABELS[field] ?? titleCase(field),
        before: NO_FUNCTIONAL_CHANGE,
        after: NO_FUNCTIONAL_CHANGE,
        changed: false,
        kind: "asset_aggregate",
      },
    ];
  }

  return out;
}

const DATE_FIELDS = new Set([
  "effectiveDate",
  "expirationDate",
  "boundDate",
  "cancellationDate",
]);

function formatDateOrEmpty(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === "") return EMPTY_DISPLAY;
  const formatted = formatDate(raw);
  if (formatted === "—") return EMPTY_DISPLAY;
  return formatted;
}

// CSR-2326 — instants rather than calendar dates. DC's change history stores
// them as Postgres timestamptz text ("2026-10-01 01:46:30.000185+00"), which
// Safari will not parse, so normalize to ISO first; then render as a date and
// time in the viewer's zone, the same shape as the sidebar's "Updated …" line.
const DATETIME_FIELDS = new Set(["aorIdentifiedAt"]);

function pgTimestampToIso(raw: string): string {
  return raw
    .trim()
    .replace(" ", "T")
    .replace(/(\.\d{3})\d+/, "$1")
    .replace(/([+-]\d{2})$/, "$1:00");
}

function formatDateTimeOrEmpty(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === "") return EMPTY_DISPLAY;
  const formatted = formatLastUpdated(pgTimestampToIso(raw));
  return formatted === "—" ? EMPTY_DISPLAY : formatted;
}

const AOR_SOURCE_DISPLAY: Record<string, string> = {
  carrier_scrape: "Automation",
  ams_manual: "Agent",
};

function formatStatusValue(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === "") return EMPTY_DISPLAY;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function formatSubstatusValue(raw: string | null | undefined): string {
  return isPolicySubstatus(raw) ? formatPolicySubstatusLabel(raw) : formatScalarValue(raw);
}

function formatScalarValue(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === "") return EMPTY_DISPLAY;
  return raw;
}

function formatScalarChange(
  field: string,
  oldValue: string | null,
  newValue: string | null,
): FormattedDiff[] {
  const label = FIELD_LABELS[field] ?? titleCase(field);

  if (field === "premium") {
    return [
      {
        label,
        before: formatCurrencyValue(oldValue),
        after: formatCurrencyValue(newValue),
        changed: true,
        kind: "currency",
      },
    ];
  }

  if (DATE_FIELDS.has(field)) {
    return [
      {
        label,
        before: formatDateOrEmpty(oldValue),
        after: formatDateOrEmpty(newValue),
        changed: true,
        kind: "date",
      },
    ];
  }

  if (DATETIME_FIELDS.has(field)) {
    return [
      {
        label,
        before: formatDateTimeOrEmpty(oldValue),
        after: formatDateTimeOrEmpty(newValue),
        changed: true,
        kind: "date",
      },
    ];
  }

  if (field === "aorSource") {
    const display = (raw: string | null) =>
      raw ? (AOR_SOURCE_DISPLAY[raw] ?? raw) : EMPTY_DISPLAY;
    return [
      {
        label,
        before: display(oldValue),
        after: display(newValue),
        changed: true,
        kind: "scalar",
      },
    ];
  }

  if (field === "status") {
    return [
      {
        label,
        before: formatStatusValue(oldValue),
        after: formatStatusValue(newValue),
        changed: true,
        kind: "status",
      },
    ];
  }

  if (field === "substatus") {
    return [
      {
        label,
        before: formatSubstatusValue(oldValue),
        after: formatSubstatusValue(newValue),
        changed: true,
        kind: "scalar",
      },
    ];
  }

  return [
    {
      label,
      before: formatScalarValue(oldValue),
      after: formatScalarValue(newValue),
      changed: true,
      kind: "scalar",
    },
  ];
}

export function formatPolicyChange(
  entry: PolicyChangeHistoryEntry,
  _policy: PolicyContext,
): FormattedDiff[] {
  // `_policy` is reserved for future locale/currency context; not used today.
  void _policy;

  const { field, oldValue, newValue, policyId } = entry;

  if (field === "coverages") {
    return diffCoverages(oldValue, newValue, policyId);
  }
  if (isAssetField(field)) {
    return diffAssetArray(field as "vehicles" | "trailers" | "drivers", oldValue, newValue, policyId);
  }
  return formatScalarChange(field, oldValue, newValue);
}

// CSR-1746 — the carrier display name used for sync attribution, or `null`
// when the policy's carrier isn't a sync-supported one. Shared by
// `deriveSourceTag` (the change-history source tag) and the Policy Activity
// tab's system-sync actor so both derive the carrier name identically; each
// caller keeps its own fallback wording ("Synced from NowCerts" vs
// "AMS System").
export function resolveCarrierSyncName(policy: PolicyContext): string | null {
  if (!isCarrierSyncSupported(policy.carrier, policy.parentCarrier ?? null)) {
    return null;
  }
  return (policy.parentCarrier && policy.parentCarrier.trim()) || policy.carrier;
}

export function deriveSourceTag(
  entry: PolicyChangeHistoryEntry,
  policy: PolicyContext,
): string {
  if (entry.source === "manual") return "Manual update";
  // CSR-1845 — AMS-side system decisions (the renewal-offer guard flip) carry
  // the Event Logging Spec's system label, never the editing user's name.
  if (entry.source === "system") return "AMS System";
  if (entry.source === "sync") {
    const name = resolveCarrierSyncName(policy);
    return name ? `Synced from ${name}` : "Synced from NowCerts";
  }
  return "Synced";
}

// CSR-1746 — the Event Logging Spec sync titles. The trigger (Manual vs System)
// is derived from `performedBy`: a real user name means a person clicked
// Refresh (manual); null means a scheduled/auto sync (system). A "COI
// triggered" variant is not derivable from `/sync-events` (that feed excludes
// the sync_for_coi-family rows and carries no trigger field), so it is omitted.
// CSR-1940 — extracted here (verbatim) from PolicyActivitiesTab so the insured
// timeline's per-policy sync fan-out renders sync runs identically.
/**
 * CSR-1977 — did this sync run succeed? `no_change` is a healthy scrape that
 * simply found nothing to update, so every "was this OK?" decision must group
 * it with `succeeded`, never with `failed`/`not_found`.
 *
 * CSR-2174 — `unattributable` joins them. The scrape ran and the carrier data
 * landed; only the proof of WHICH of two live terms it belongs to is missing.
 * Treating it as a failure here would put an alert triangle and a "Policy Sync
 * — Failed" card on ~45% of the GEICO book, which is the lie this ticket exists
 * to delete.
 *
 * Shared because three surfaces branch on it — the title below, the Policy
 * Activity tab's icon, and the insured timeline's run-card filter — and a copy
 * that misses a member silently renders a healthy run as a failure.
 */
export function isSuccessfulSyncOutcome(
  outcome: PolicySyncEvent["outcome"],
): boolean {
  return (
    outcome === "succeeded" ||
    outcome === "no_change" ||
    outcome === "unattributable"
  );
}

// CSR-1977 — `no_change` is a successful run, so it keeps the trigger-based
// title; only genuine non-success outcomes collapse to "Failed". The
// no-change result itself is stated in the detail line below.
export function syncEventTitle(evt: PolicySyncEvent): string {
  if (isSuccessfulSyncOutcome(evt.outcome)) {
    return evt.performedBy
      ? "Policy Sync — Manually triggered"
      : "Policy Sync — System triggered";
  }
  return "Policy Sync — Failed";
}

// CSR-1746 — the failure reason / removed note preserved as the detail line, so
// the not_found / failed / removed distinction survives the title collapse.
export function syncEventDetail(evt: PolicySyncEvent): string | undefined {
  if (evt.outcome === "not_found") {
    return evt.error ?? "Policy not found at the carrier during the last sync.";
  }
  if (evt.outcome === "failed") {
    return evt.error ?? "The carrier sync did not complete.";
  }
  if (evt.removed) return "Policy removed from Data Center.";
  // CSR-1977 — the timeline is where an agent checks "did that sync actually
  // do anything?", so say so explicitly instead of leaving a bare title.
  if (evt.outcome === "no_change") {
    return "No changes — the carrier data already matched what we have.";
  }
  // CSR-2174 — same question, different answer: the run finished, and this line
  // is the only place that says why it could not be pinned to this term.
  if (evt.outcome === "unattributable") {
    return syncAttemptUnavailableMessage(evt.unattributableReason);
  }
  return undefined;
}

// CSR-1746 — attribution for a system-triggered sync: "Automated sync from
// <Carrier>" when the carrier is sync-supported (reusing the shared carrier-name
// logic), else the spec's unknown-system label "AMS System".
export function systemSyncActor(policy: PolicyContext): string {
  const name = resolveCarrierSyncName(policy);
  return name ? `Automated sync from ${name}` : "AMS System";
}

export function localDayKey(value: Date | string): string {
  const d = toDate(value);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatEventTime(value: Date | string): string {
  const d = toDate(value);
  return formatNoteDateTime(d);
}

// ---------------- CSR-1843: shared change-history → card builder ----------------
//
// The grouping/title/bullet/source/actor logic below was previously inlined in
// `PolicyActivitiesTab.buildPolicyEvents`. It is promoted here so BOTH the Policy
// Activity tab AND the Insured Overview timeline render policy-change cards from a
// single implementation (cross-surface identical rendering is the point of the
// ticket). Callers own their own day-grouping/sorting.

export function diffToBullet(diff: FormattedDiff): string {
  if (diff.kind === "asset_aggregate" || diff.kind === "asset_item") {
    return diff.label;
  }
  if (!diff.changed) {
    return `${diff.label} — no functional change`;
  }
  return `${diff.label} changed from ${diff.before} to ${diff.after}`;
}

// CSR-1746 — the display-layer no-op predicate. A diff whose formatted
// before/after are identical (e.g. an Effective Date "07/12/2026 →
// 07/12/2026" from an ISO/format-only delta) carries no visible change and is
// dropped from the card. Asset add/remove/modify lines intentionally carry
// their content in `label` with empty before/after, so for those kinds only
// the defensive "no functional change" fallback (`changed === false`) is a
// no-op — a genuine asset line (`changed === true`) always survives.
export function isNoOpDiff(diff: FormattedDiff): boolean {
  if (diff.kind === "asset_aggregate" || diff.kind === "asset_item") {
    return diff.changed === false;
  }
  return diff.before === diff.after;
}

export interface PolicyChangeGroup {
  id: string;
  changedAt: string;
  title: string;
  sourceTag: string;
  actor: string | null;
  note: string | null;
  changeLines: string[];
  // CSR-1940 — the raw change-history `source` (sync/manual/system) so the
  // insured timeline can tag sync-sourced change cards as sync events without
  // re-deriving it from the rendered sourceTag string.
  source: string;
}

export function buildPolicyChangeGroups(
  changeHistory: PolicyChangeHistoryEntry[],
  policy: PolicyContext,
): PolicyChangeGroup[] {
  const groups = new Map<string, PolicyChangeHistoryEntry[]>();
  for (const entry of changeHistory) {
    // CSR-1955 — suppress the raw deltas of a record update; it renders as its
    // own labeled card from the dedicated feed (no-op unless that flag is ON).
    if (entry.dcSource === "record_update") continue;
    const list = groups.get(entry.changedAt) ?? [];
    list.push(entry);
    groups.set(entry.changedAt, list);
  }

  const out: PolicyChangeGroup[] = [];
  for (const [changedAt, entries] of Array.from(groups.entries())) {
    // CSR-1746 — build the change lines first, dropping formatted no-ops and
    // exact-duplicate scalar bullets, so the title reflects only the fields
    // that actually produced a visible line (a suppressed field never inflates
    // the "N fields updated" count).
    const bullets: string[] = [];
    const seen = new Set<string>();
    const survivingFields = new Set<string>();
    for (const e of entries) {
      let survived = false;
      for (const diff of formatPolicyChange(e, policy)) {
        if (isNoOpDiff(diff)) continue;
        const bullet = diffToBullet(diff);
        const isAsset =
          diff.kind === "asset_aggregate" || diff.kind === "asset_item";
        // Dedup scalar/date/coverage bullets only; never collapse repeated
        // asset labels (two identical vehicles are two real rows).
        if (!isAsset) {
          if (seen.has(bullet)) continue;
          seen.add(bullet);
        }
        bullets.push(bullet);
        survived = true;
      }
      if (survived) survivingFields.add(FIELD_LABELS[e.field] ?? e.field);
    }

    const fields = Array.from(survivingFields);
    const title =
      fields.length === 1
        ? `${fields[0]} updated`
        : fields.length > 1
        ? `${fields.length} fields updated`
        : "Policy updated";
    const note = entries.find((e) => e.note)?.note ?? null;

    // CSR-1746 — a group with no surviving change line AND no note is a phantom
    // card (e.g. an Effective Date "07/12/2026 → 07/12/2026" format-only delta).
    // Drop it. A note-only group is kept — a note is meaningful content.
    if (bullets.length === 0 && !note) continue;

    out.push({
      id: entries[0].id,
      changedAt,
      title,
      sourceTag: deriveSourceTag(entries[0], policy),
      actor: entries[0].changedByName ?? null,
      note,
      changeLines: bullets,
      source: entries[0].source,
    });
  }
  return out;
}
